import React, { useState } from 'react';
import { HeartIcon, InfoIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { useRomanceTagging } from '../../../../hooks/useRomanceTagging';
import { SearchWithAutocomplete, Suggestion } from '../../../../components/UI/SearchWithAutocomplete';
import { ROMANCE_MODULE_DATA, SPICE_LEVEL_CARDS, DARKNESS_LEVEL_CARDS, BURN_LEVEL_CARDS } from '../genreModuleData';
import { PAIRING_TYPES, ROMANCE_TROPES } from '../../../../data/romanceTaggingData';
import { SearchableChipField } from './shared';
// Build pairing type suggestions
const pairingTypeSuggestions: Suggestion[] = PAIRING_TYPES.map(pt => ({
  id: pt.toLowerCase().replace(/[\s\/]+/g, '-'),
  label: pt,
  type: 'Pairing'
}));
const ACCENT = 'bg-pink-100 text-pink-700 border-pink-500 dark:bg-pink-900/30 dark:text-pink-300';
// ─── Pairing Type Search Field (reusable) ───
const PairingTypeField: React.FC<{
  pairingType: string;
  setPairingType: (v: string) => void;
  required?: boolean;
}> = ({
  pairingType,
  setPairingType,
  required
}) => {
  const dk = useDarkClasses();
  const [search, setSearch] = useState('');
  return <div data-id="element-21309" data-mp="2vrq2dla9yh5r">
      <label className={`flex items-center text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-21310" data-mp="2vrga1r1rbxww">
        Pairing Type
        {required && <span className="ml-2 text-xs text-pink-500 font-semibold px-2 py-0.5 bg-pink-50 dark:bg-pink-900/20 rounded-full" data-id="element-21311" data-mp="2rojwwfuzku0c">
            Required
          </span>}
      </label>
      {pairingType ? <div className="flex items-center gap-2 mt-1" data-id="element-21312" data-mp="2ctdstyfgb59n">
          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${ACCENT}`} data-id="element-21313" data-mp="2cu5eagfc3mm7">
            {pairingType}
            <button onClick={() => setPairingType('')} className="ml-1.5 hover:opacity-75" data-id="element-21314" data-mp="b1r1fvrnd8cn">
              <XIcon className="w-3 h-3" data-id="element-21315" data-mp="71qj401fgd0b" />
            </button>
          </span>
        </div> : <SearchWithAutocomplete value={search} onChange={setSearch} suggestions={pairingTypeSuggestions} placeholder="e.g. M/F, Why Choose, Reverse Harem..." onSelect={item => {
      setPairingType(item.label);
      setSearch('');
    }} data-id="element-21316" data-mp="20icsvunsh0ug" />}
    </div>;
};
// ═══════════════════════════════════════════════════
// FULL ROMANCE MODULE
// ═══════════════════════════════════════════════════
interface RomanceModuleProps {
  pairingType: string;
  setPairingType: (v: string) => void;
  tropes: string[];
  setTropes: React.Dispatch<React.SetStateAction<string[]>>;
  characterPairings: string[];
  setCharacterPairings: React.Dispatch<React.SetStateAction<string[]>>;
  endingType: string;
  setEndingType: (v: string) => void;
  spiceLevel: number;
  setSpiceLevel: (v: number) => void;
  burnLevel: number;
  setBurnLevel: (v: number) => void;
  darknessLevel: number;
  setDarknessLevel: (v: number) => void;
  warnings: string[];
  setWarnings: React.Dispatch<React.SetStateAction<string[]>>;
}
export const RomanceModule: React.FC<RomanceModuleProps> = ({
  pairingType,
  setPairingType,
  tropes,
  setTropes,
  characterPairings,
  setCharacterPairings,
  endingType,
  setEndingType,
  spiceLevel,
  setSpiceLevel,
  burnLevel,
  setBurnLevel,
  darknessLevel,
  setDarknessLevel,
  warnings,
  setWarnings
}) => {
  const dk = useDarkClasses();
  const romanceTagging = useRomanceTagging();
  const toggleTrope = (t: string) => setTropes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const togglePairing = (p: string) => setCharacterPairings(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const toggleWarning = (w: string) => setWarnings(prev => prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w]);
  const hasFlaggedTrope = tropes.some(t => ROMANCE_TROPES.find(def => def.name === t)?.isFlagged);
  return <div className="space-y-8" data-id="element-21317" data-mp="2obmtbbckamy2">
      {/* Pairing Type */}
      <PairingTypeField pairingType={pairingType} setPairingType={setPairingType} required data-id="element-21318" data-mp="2t83hu6nob3bo" />

      {/* Romance Tropes */}
      <SearchableChipField label="Romance Tropes" placeholder="e.g. Enemies to Lovers, Second Chance, Fake Dating..." items={ROMANCE_MODULE_DATA.tropes} selected={tropes} onToggle={toggleTrope} accentClass={ACCENT} data-id="element-21319" data-mp="2x8do7kfdnvr2" />
      {hasFlaggedTrope && <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 flex items-start" data-id="element-21320" data-mp="3c17d1wgjho8e">
          <InfoIcon className="w-4 h-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-21321" data-mp="3l0kkykp0m4r0" />
          <p className="text-xs text-amber-800 dark:text-amber-200" data-id="element-21322" data-mp="1mkpra85yb7yo">
            You've selected tropes that may include power imbalance dynamics.
            This book may be classified as AfterTide.
          </p>
        </div>}

      {/* Character Pairings */}
      <SearchableChipField label="Character Pairings" placeholder="Search pairings..." items={ROMANCE_MODULE_DATA.characterPairings} selected={characterPairings} onToggle={togglePairing} accentClass={ACCENT} data-id="element-21323" data-mp="1j4lj8e5sh4wo" />

      {/* Ending Type */}
      <div data-id="element-21324" data-mp="15cyle9w77obs">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21325" data-mp="159147tt6l6uj">
          Ending Type
        </label>
        <div className="flex flex-wrap gap-2" data-id="element-21326" data-mp="1x2a2xio4e605">
          {ROMANCE_MODULE_DATA.endingTypes.map(ending => <button key={ending} onClick={() => setEndingType(ending)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border
                ${endingType === ending ? 'bg-green-100 text-green-700 border-green-500 dark:bg-green-900/30 dark:text-green-300' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border}`}`} data-id="element-21327" data-mp="1t06ruv5d53fe">
              {ending}
            </button>)}
        </div>
      </div>

      {/* ─── Spice Level (5-card layout) ─── */}
      <div data-id="element-21328" data-mp="j1nev4d0baqe">
        <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-21329" data-mp="j26pe00buwue">
          Spice Level
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-21330" data-mp="550a4fetbg9x">
          {SPICE_LEVEL_CARDS.map(card => {
          const isSelected = spiceLevel === card.level;
          return <button key={card.level} onClick={() => setSpiceLevel(isSelected ? 0 : card.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? card.selectedBg : `${dk.surfaceMuted} ${dk.border} hover:border-red-300`}`} data-id="element-21331" data-mp="3rgp24f17r0kc">
                <div className={`flex items-center gap-1.5 mb-1.5`} data-id="element-21332" data-mp="38j4dn92d2409">
                  <span className="text-base" data-id="element-21333" data-mp="38m0cczm18wpr">{card.emoji}</span>
                  <span className={`font-bold text-sm ${isSelected ? '' : dk.textPrimary}`} data-id="element-21334" data-mp="3di7j2um96c8r">
                    {card.name}
                  </span>
                </div>
                <p className={`text-[11px] leading-tight ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21335" data-mp="2ozozmobyd3ak">
                  {card.description}
                </p>
              </button>;
        })}
        </div>
        {spiceLevel === 0 && <p className={`text-xs mt-2 ${dk.textMuted}`} data-id="element-21336" data-mp="3cu8imdyds8td">
            Select a spice level (click again to remove)
          </p>}
      </div>

      {/* ─── Burn Level (3-card layout) ─── */}
      <div data-id="element-21337" data-mp="3mkbcegdbtqsr">
        <label className={`block text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-21338" data-mp="3mktdpreuiv7j">
          Burn Level
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-21339" data-mp="3hovy5nbepsvt">
          {BURN_LEVEL_CARDS.map(card => {
          const isSelected = burnLevel === card.level;
          return <button key={card.level} onClick={() => setBurnLevel(isSelected ? 0 : card.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? card.selectedBg : `${dk.surfaceMuted} ${dk.border} hover:border-amber-300`}`} data-id="element-21340" data-mp="3ql4xz33aqtih">
                <div className={`flex items-center gap-1.5 mb-1.5`} data-id="element-21341" data-mp="1ftse3c2hv4na">
                  <span className="text-base" data-id="element-21342" data-mp="1fry24n97amvc">{card.emoji}</span>
                  <span className={`font-bold text-sm ${isSelected ? '' : dk.textPrimary}`} data-id="element-21343" data-mp="j52vtkmyga0s">
                    {card.name}
                  </span>
                </div>
                <p className={`text-[11px] leading-tight ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21344" data-mp="s2wswxsjn7p0">
                  {card.description}
                </p>
              </button>;
        })}
        </div>
        {burnLevel === 0 && <p className={`text-xs mt-2 ${dk.textMuted}`} data-id="element-21345" data-mp="4dkk3z14p1n8">
            How quickly does the romance develop?
          </p>}
      </div>

      {/* ─── Darkness Level (5-card layout, matching SpiceDarkness.tsx) ─── */}
      <div data-id="element-21346" data-mp="3o2gpowahkvk3">
        <div className="flex items-center justify-between mb-3" data-id="element-21347" data-mp="3nzul95iw6w63">
          <label className={`text-sm font-medium ${dk.textSecondary}`} data-id="element-21348" data-mp="3j19hwx8cfwdf">
            Darkness Level
          </label>
          {romanceTagging.classification.darkness_required && <span className="text-xs text-red-500 font-semibold px-2 py-0.5 bg-red-50 dark:bg-red-900/20 rounded-full" data-id="element-21349" data-mp="3a2xwd9mc3oru">
              Required based on tags
            </span>}
        </div>
        {romanceTagging.showDarknessPrompt && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800 flex items-start" data-id="element-21350" data-mp="309qh8x1skuek">
            <AlertTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-21351" data-mp="2rbo9zzrgt5iu" />
            <p className="text-sm text-red-800 dark:text-red-200 font-medium" data-id="element-21352" data-mp="2mbs6e1mojg3t">
              Your selected tags suggest this book may contain darker themes.
              Please confirm the darkness level.
            </p>
          </div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-21353" data-mp="6e6zv2not6gj">
          {DARKNESS_LEVEL_CARDS.map(card => {
          const isSelected = darknessLevel === card.level;
          return <button key={card.level} onClick={() => setDarknessLevel(isSelected ? 0 : card.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? card.selectedBg : `${dk.surfaceMuted} ${dk.border} hover:border-slate-400`}`} data-id="element-21354" data-mp="g6d172ztx08e">
                <div className={`flex items-center gap-1.5 mb-1.5`} data-id="element-21355" data-mp="3tin7egh0cy4n">
                  <span className="text-base" data-id="element-21356" data-mp="3tlj6470ojqu5">{card.emoji}</span>
                  <span className={`font-bold text-sm ${isSelected ? '' : dk.textPrimary}`} data-id="element-21357" data-mp="1dqpn758c3cju">
                    {card.name}
                  </span>
                </div>
                <p className={`text-[11px] leading-tight ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21358" data-mp="1gavqrbt1d0zv">
                  {card.description}
                </p>
              </button>;
        })}
        </div>
        {darknessLevel === 0 && <p className={`text-xs mt-2 ${dk.textMuted}`} data-id="element-21359" data-mp="1bdvsvo6cb4ju">
            Select a darkness level (click again to remove)
          </p>}
      </div>

      {/* Romance-Specific Warnings */}
      <SearchableChipField label="Romance-Specific Warnings" placeholder="e.g. Dubious Consent, Stalking, Captivity..." items={ROMANCE_MODULE_DATA.warnings} selected={warnings} onToggle={toggleWarning} accentClass="bg-red-100 text-red-700 border-red-500 dark:bg-red-900/30 dark:text-red-300" data-id="element-21360" data-mp="240q0hki269fw" />

      {/* Classification Preview */}
      <div className={`p-5 rounded-xl border-2 ${romanceTagging.classification.aftertide_eligible ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-700' : `border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50`}`} data-id="element-21361" data-mp="2mtkg8gnia5l6">
        <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 ${dk.textMuted}`} data-id="element-21362" data-mp="2nnkurbdvmp8l">
          Classification Preview
        </h3>
        <div className="space-y-3" data-id="element-21363" data-mp="2iohzginp7xdt">
          <div data-id="element-21364" data-mp="2dpes2rk8xf2o">
            <span className={`font-semibold text-sm ${romanceTagging.classification.aftertide_eligible ? 'text-amber-600 dark:text-amber-400' : dk.textPrimary}`} data-id="element-21365" data-mp="2dq6lpwua92qb">
              {romanceTagging.classification.aftertide_eligible ? 'AfterTide Classified' : 'Standard Catalog'}
            </span>
            {romanceTagging.classification.aftertide_reasons.length > 0 && <ul className="list-disc list-inside pl-4 mt-1" data-id="element-21366" data-mp="1uynn027akau3">
                {romanceTagging.classification.aftertide_reasons.map((reason, i) => <li key={i} className={`text-xs ${dk.textSecondary}`} data-id="element-21367" data-mp="1fwx8r3og37qb" data-mpi={i}>
                      {reason}
                    </li>)}
              </ul>}
          </div>
          <div className={`p-3 rounded-lg bg-white dark:bg-slate-900 border ${dk.border} shadow-sm`} data-id="element-21368" data-mp="1pvczh5jk8eza">
            <span className={`block text-xs font-semibold mb-2 ${dk.textSecondary}`} data-id="element-21369" data-mp="1usmzff4ks91d">
              Card Overlay Preview:
            </span>
            <div className="flex flex-wrap items-center gap-2" data-id="element-21370" data-mp="125sgwnrbjjqj">
              <span className={`font-bold text-sm ${dk.textPrimary}`} data-id="element-21371" data-mp="1743sb2x9i2av">
                {pairingType || 'Pairing'}
              </span>
              {tropes.length > 0 && <>
                  <span className={dk.textMuted} data-id="element-21372" data-mp="ef69myp523hl">•</span>
                  <span className={`text-sm ${dk.textSecondary}`} data-id="element-21373" data-mp="een28bx46xht">
                    {tropes.slice(0, 3).join(' • ')}
                  </span>
                </>}
              {romanceTagging.classification.overlay_flags.showAfterTide && <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-xs font-bold text-amber-700 dark:text-amber-400" data-id="element-21374" data-mp="2g4u9994dsq0u">
                  AfterTide
                </span>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
// ═══════════════════════════════════════════════════
// COMPACT ROMANCE MODULE
// ═══════════════════════════════════════════════════
interface RomanceModuleCompactProps {
  pairingType: string;
  setPairingType: (v: string) => void;
  tropes: string[];
  setTropes: React.Dispatch<React.SetStateAction<string[]>>;
  endingType: string;
  setEndingType: (v: string) => void;
}
export const RomanceModuleCompact: React.FC<RomanceModuleCompactProps> = ({
  pairingType,
  setPairingType,
  tropes,
  setTropes,
  endingType,
  setEndingType
}) => {
  const dk = useDarkClasses();
  const toggleTrope = (t: string) => setTropes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  return <div className="space-y-5" data-id="element-21375" data-mp="3v1y7nt7byxu8">
      {/* Pairing Type */}
      <PairingTypeField pairingType={pairingType} setPairingType={setPairingType} data-id="element-21376" data-mp="3v3hobkwwfawj" />

      {/* 1-3 Tropes */}
      <SearchableChipField label="Romance Tropes (up to 3)" placeholder="e.g. Enemies to Lovers, Second Chance..." items={ROMANCE_MODULE_DATA.tropes} selected={tropes} onToggle={t => {
      if (tropes.includes(t)) {
        setTropes(prev => prev.filter(x => x !== t));
      } else if (tropes.length < 3) {
        setTropes(prev => [...prev, t]);
      }
    }} accentClass={ACCENT} data-id="element-21377" data-mp="3lbbxieiduzix" />

      {/* Ending Type */}
      <div data-id="element-21378" data-mp="vmjiovgk3xp4">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21379" data-mp="vm1m1xpzfhgo">
          Ending Type
        </label>
        <div className="flex flex-wrap gap-2" data-id="element-21380" data-mp="10k36on323ou9">
          {ROMANCE_MODULE_DATA.endingTypes.map(ending => <button key={ending} onClick={() => setEndingType(ending)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border
                ${endingType === ending ? 'bg-green-100 text-green-700 border-green-500 dark:bg-green-900/30 dark:text-green-300' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border}`}`} data-id="element-21381" data-mp="z5saiv6o352w">
              {ending}
            </button>)}
        </div>
      </div>
    </div>;
};